import * as Blockly from 'blockly/core'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { createPortal, flushSync } from 'react-dom'
import {
  MdCloseFullscreen,
  MdOpenInFull,
  MdOpenInNew,
  MdRemove,
} from 'react-icons/md'
import { Rnd } from 'react-rnd'

import { useBringToFront } from '@/hooks/useBringToFront'
import { CAMERA_MODE_LABEL } from '../../preview/cameraModeLabel'
import { PreviewPanel } from '../../preview/PreviewPanel'
import { CameraMode } from '../../preview/render'
import { SceneDescriptor } from '../../preview/types'
import { useDevToolsEnabled } from '../devTools/useDevToolsEnabled'
import { PreviewRunControls } from './PreviewRunControls'
import { usePreviewControl } from './usePreviewControl'
import './previewModal.css'

const MIN_WIDTH = 360
const MIN_HEIGHT = 280
// 初期表示はコンパクト（編集領域を圧迫しない）。最小サイズと同値にし、
// 必要に応じて左下方向へ広げてもらう運用にする。
const INITIAL_WIDTH = 360
const INITIAL_HEIGHT = 280
/**
 * リサイズ可能な辺・角。右上（tab-bar 下ボーダーの右端）へ固定するため、上端・右端と
 * 上側の角は無効化し、左辺・下辺・左下角だけで左下方向に広げられるようにする
 * （右下角を無効にしてフッター右の PIP/最小化ボタンとの誤操作を避ける）。
 */
const RESIZE_ENABLE = {
  top: false,
  right: false,
  bottom: true,
  left: true,
  topRight: false,
  bottomRight: false,
  bottomLeft: true,
  topLeft: false,
} as const
/**
 * 下辺ハンドルはフッター右の操作ボタンへ被らないよう右側 120px を空ける
 * （リサイズは左下角・左辺・下辺左寄りに集約）。
 * 左下角ハンドルはフッター左端のリサイズグリップ（⤡ アイコン）を覆う大きさに
 * 広げ、「アイコンを押しながらドラッグで拡大縮小」できるようにする。
 * グリップ自体は pointer-events: none の視覚表示で、実操作はこのハンドルが担う。
 */
const RESIZE_HANDLE_STYLES = {
  bottom: { width: 'calc(100% - 120px)' },
  bottomLeft: {
    width: 48,
    height: 44,
    left: 0,
    bottom: 0,
    cursor: 'nesw-resize',
  },
} as const

/** tab-bar（「コードを見る」ボタンを含む上部バー）のセレクタ。 */
const TAB_BAR_SELECTOR = '.tab-bar'
/** tab-bar を取得できないときに使う上端フォールバック（右端基準）。 */
const ANCHOR_FALLBACK_TOP = 120

// Document Picture-in-Picture API（実験的・Chromium 系のみ）。
interface DocumentPictureInPicture {
  requestWindow(options?: {
    width?: number
    height?: number
    disallowReturnToOpener?: boolean
  }): Promise<Window>
  readonly window: Window | null
}

declare global {
  interface Window {
    documentPictureInPicture?: DocumentPictureInPicture
  }
}

export interface PreviewModalProps {
  scene: SceneDescriptor
  workspaces: Map<string, Blockly.Workspace>
  /** 値が変わると PreviewPanel を再マウントしてスナップショットを差し替える。 */
  refreshKey: number
}

const clampPosition = (
  x: number,
  y: number,
  width: number,
  height: number,
): { x: number; y: number } => ({
  x: Math.max(0, Math.min(x, Math.max(0, window.innerWidth - width))),
  y: Math.max(0, Math.min(y, Math.max(0, window.innerHeight - height))),
})

/**
 * 「コードを見る」ボタンを含む tab-bar の下ボーダーに上端を、右端に右端を合わせた
 * 右上の固定位置を返す。tab-bar が見つからないときはウィンドウ右端へフォールバック。
 */
const computeAnchorPosition = (
  width: number,
  height: number,
): { x: number; y: number } => {
  const tabBar = document.querySelector(TAB_BAR_SELECTOR)
  const rect = tabBar?.getBoundingClientRect()
  const right = rect ? rect.right : window.innerWidth
  const top = rect ? rect.bottom : ANCHOR_FALLBACK_TOP
  return clampPosition(right - width, top, width, height)
}

/** メインドキュメントの CSS を PIP 窓へコピーする（スタイル欠落防止）。 */
const copyStylesToPipDocument = (pipDoc: Document): void => {
  for (const node of Array.from(
    document.head.querySelectorAll('style, link[rel="stylesheet"]'),
  )) {
    pipDoc.head.appendChild(node.cloneNode(true))
  }
  pipDoc.body.style.margin = '0'
  pipDoc.body.style.height = '100vh'
  pipDoc.body.style.background = '#000'
}

interface PreviewMinimizedTabProps {
  onClick: () => void
}

/**
 * 最小化時の右端タブ。`videoModal` の windowMinimized と揃った見た目。
 * video の minimized タブ（bottom: 200px）の上（bottom: 280px）に配置して
 * 視覚的に積み上げる。アイコンはおためしプレイを表すキューブ。
 */
const PreviewMinimizedTab = ({ onClick }: PreviewMinimizedTabProps) => (
  <button
    type="button"
    className="preview-modal-minimized-tab"
    onClick={onClick}
    aria-label="おためしプレイ を ひらく"
    title="おためしプレイ"
  >
    <span className="preview-modal-minimized-inner">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinejoin="round"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M12 2.5 21 7.25v9.5L12 21.5 3 16.75v-9.5z" />
        <path d="M3 7.25 12 12l9-4.75" />
        <path d="M12 12v9.5" />
      </svg>
    </span>
  </button>
)

/**
 * react-rnd のウィンドウ内に `PreviewPanel` を内包するパネル。
 *
 * 「コードを見る」ボタンを含む tab-bar の下ボーダーへ右上を固定（ドラッグ不可）し、
 * リサイズのみ可。最小化（video と同じ右端タブ式）・PIP 対応。
 * 再生/停止/リセットはメインヘッダー（HeaderMenu）に、視点切替/PIP/最小化は
 * フッターに配置する。
 */
export const PreviewModal = ({
  scene,
  workspaces,
  refreshKey,
}: PreviewModalProps) => {
  const [size, setSize] = useState({
    width: INITIAL_WIDTH,
    height: INITIAL_HEIGHT,
  })
  // 初期値は右端フォールバック。実際の固定位置は tab-bar 計測後に layout effect で確定。
  const [position, setPosition] = useState(() =>
    clampPosition(
      window.innerWidth - INITIAL_WIDTH,
      ANCHOR_FALLBACK_TOP,
      INITIAL_WIDTH,
      INITIAL_HEIGHT,
    ),
  )
  const [minimized, setMinimized] = useState(false)
  const [pipWindow, setPipWindow] = useState<Window | null>(null)
  // パネルの命令的ハンドルは共有 context 経由で持つ。
  // 再生/停止/リセットはメインヘッダー（HeaderMenu）から、視点切替は
  // このモーダルのヘッダーから、同一の panelRef を介して操作する。
  const {
    panelRef,
    setReady: setControlReady,
    setRunning: setControlRunning,
    setVolume: setControlVolume,
  } = usePreviewControl()
  // 視点切替は開発者ツール限定（製品仕様はアバター視点のみ）。
  const devToolsEnabled = useDevToolsEnabled()
  // ヘッダーの視点ラベル表示用。PreviewPanel から通知される現在モードを保持。
  const [cameraMode, setCameraMode] = useState<CameraMode>('follow')
  const { order, bringToFront } = useBringToFront()

  const pipSupported =
    typeof window !== 'undefined' && Boolean(window.documentPictureInPicture)

  // PreviewPanel の再マウント単位（key）。refreshKey 変更（スナップショット差し替え）
  // でのみ作り直す。PIP 切替は再マウントせず、ホスト DOM ごと窓間移動して状態を保つ。
  const panelKey = `${refreshKey}`

  // PreviewPanel を一度だけマウントする React 管理外のホスト div。
  // PIP 切替時はこの div を窓間で appendChild 移動し、World・物理・実行状態を維持する。
  // useState の遅延初期化で生成し、コンポーネント生存中は同一ノードを使い続ける。
  const [panelHost] = useState<HTMLDivElement>(() => {
    const host = document.createElement('div')
    // ホストはスロットいっぱいに広げ、内部の PreviewPanel（100%）を満たす。
    host.style.width = '100%'
    host.style.height = '100%'
    return host
  })

  // パネル内（canvas 等）のクリックでも窓を前面化する。PreviewPanel は React
  // 管理外のホスト div へ portal しているため、Rnd の onMouseDown（React 合成
  // イベント）にはパネル内の操作が届かない。ホスト div へネイティブの
  // pointerdown を張って補完する（PIP へ移動してもリスナは要素に追従する）。
  useEffect(() => {
    panelHost.addEventListener('pointerdown', bringToFront)
    return () => {
      panelHost.removeEventListener('pointerdown', bringToFront)
    }
  }, [panelHost, bringToFront])

  // ready を「現在の panel 実体（key と scene）」と結びつけて派生で持つ。
  // key/scene が変わると同じ commit で即 ready=false になるため、切替直後に
  // 古い再生ボタンが一瞬残り、切替中の panelRef へ命令が飛ぶ競合を防げる
  // （passive effect で false に戻す方式だと 1 フレーム遅れる）。
  // PIP 切替は panelKey を変えないため ready は維持され、状態リセットも起きない。
  const panelIdentity = useMemo(() => ({ panelKey, scene }), [panelKey, scene])
  const [readyIdentity, setReadyIdentity] = useState<object | null>(null)
  const ready = readyIdentity === panelIdentity

  // ヘッダーの再生/停止/リセット（PreviewRunControls）へ ready を共有する。
  // 切替直後に「有効化されたまま」の 1 フレームを残さないよう、paint 前に同期する
  // layout effect で反映し、アンマウント時は false へ戻す。
  useLayoutEffect(() => {
    setControlReady(ready)
    return () => setControlReady(false)
  }, [ready, setControlReady])

  // 「コードを見る」ボタン下のボーダー（tab-bar 下端）に右上を固定する。
  // ウィンドウや tab-bar の幅・高さ変化、サイズ変更に追従して再計算する。
  useLayoutEffect(() => {
    const anchor = () =>
      setPosition(computeAnchorPosition(size.width, size.height))
    anchor()
    window.addEventListener('resize', anchor)
    return () => window.removeEventListener('resize', anchor)
  }, [size.height, size.width])

  // PIP 窓が閉じられたら state を戻す。コンポーネント破棄時も明示的に閉じる。
  useEffect(() => {
    if (!pipWindow) {
      return
    }
    // pagehide（窓がまだ生きている間）に同期レンダーで portal をメイン窓へ
    // 戻す。通常の setState はバッチされ、React のアンマウント DOM 操作が
    // 閉じられた後の document に走って例外になる（PIP の閉じるボタンでの
    // エラーの原因）。
    const handleClose = (): void => {
      flushSync(() => setPipWindow(null))
    }
    pipWindow.addEventListener('pagehide', handleClose)
    return () => {
      pipWindow.removeEventListener('pagehide', handleClose)
    }
  }, [pipWindow])

  useEffect(() => {
    return () => {
      pipWindow?.close()
    }
  }, [pipWindow])

  const handleResize = useCallback(
    (
      _e: MouseEvent | TouchEvent,
      _dir: string,
      ref: HTMLElement,
      _delta: { width: number; height: number },
      nextPosition: { x: number; y: number },
    ) => {
      const width = parseFloat(ref.style.width)
      const height = parseFloat(ref.style.height)
      setSize({ width, height })
      setPosition(clampPosition(nextPosition.x, nextPosition.y, width, height))
    },
    [],
  )

  const minimize = useCallback((): void => setMinimized(true), [])
  const restore = useCallback((): void => setMinimized(false), [])
  const handleReady = useCallback((): void => {
    setReadyIdentity(panelIdentity)
  }, [panelIdentity])

  // PreviewPanel を載せるホスト div の置き場（スロット）。PIP 切替で content が
  // 別ドキュメントへ portal されるとスロット DOM ノードも作り直されるため、
  // ノードを state に持って差し替えを検知し、ホストを新スロットへ移す。
  const [panelSlot, setPanelSlot] = useState<HTMLDivElement | null>(null)
  const handleSlotRef = useCallback((node: HTMLDivElement | null): void => {
    setPanelSlot(node)
  }, [])

  // ホスト div を現在のスロットへ appendChild する。スロットが変わる（=窓間移動）
  // たびに移すことで、PreviewPanel を再マウントせず WebGL canvas ごと窓を渡る。
  // paint 前に移したいので layout effect を使う（ちらつき防止）。
  useLayoutEffect(() => {
    if (panelSlot && panelHost.parentNode !== panelSlot) {
      panelSlot.appendChild(panelHost)
      // 窓間移動後はキー入力を取りこぼさないよう、ビューポートへフォーカスを戻す。
      panelRef.current?.focusViewport()
    }
  }, [panelSlot, panelHost, panelRef])

  const enterPip = useCallback(async (): Promise<void> => {
    const dpip = window.documentPictureInPicture
    if (!dpip) {
      return
    }
    try {
      const win = await dpip.requestWindow({
        width: Math.max(MIN_WIDTH, size.width),
        height: Math.max(MIN_HEIGHT, size.height),
      })
      copyStylesToPipDocument(win.document)
      setPipWindow(win)
    } catch (error) {
      console.error('PIP の起動に失敗しました', error)
    }
  }, [size.height, size.width])

  const exitPip = useCallback((): void => {
    const win = pipWindow
    if (!win) {
      return
    }
    // 先に portal をメイン窓へ戻し、レンダー（パネルホストの窓間移動）が
    // 終わってから閉じる。close() を先に呼ぶと pagehide 中の flushSync が
    // React のイベント処理中はバッチに落ちて効かず、閉じた後の document
    // から canvas を移動することになり、メイン窓のビューポートが真っ白に
    // なる（ネイティブの×ボタン＝React 外起点の pagehide では起きない）。
    setPipWindow(null)
    setTimeout(() => win.close(), 0)
  }, [pipWindow])

  const content = (
    <div
      className={`preview-modal-window${pipWindow ? ' is-pip' : ''}`}
      // フッターのボタン・リサイズグリップ・ビューポートのどこを触っても
      // この窓を前面化する。capture フェーズで拾うことで、stopPropagation
      // する子要素（ボタン等）やドラッグ開始でも確実に発火させる
      // （Rnd の onMouseDown は disableDragging では発火しない）。
      onPointerDownCapture={bringToFront}
    >
      {/* ヘッダーは廃止。左上固定（ドラッグ不可）のため操作系は全てフッターに集約。 */}
      <div className="preview-modal-body">
        {/* PreviewPanel 本体は React 管理外のホスト div（panelHostRef）に portal で
            一度だけマウントし、このスロットへ appendChild で載せる。PIP 切替では
            ホスト div ごと窓間移動するため、再マウントされず状態が維持される。 */}
        <div
          className="preview-modal-panel-slot"
          ref={handleSlotRef}
          // canvas が下層、オーバーレイ操作が上層になるよう基準と重なり順を整える。
          style={{ width: '100%', height: '100%' }}
        />
        {!ready && (
          <div
            className="preview-modal-loading"
            role="status"
            aria-live="polite"
          >
            <span className="preview-modal-spinner" aria-hidden="true" />
            <span className="preview-modal-loading-text">よみこみちゅう…</span>
          </div>
        )}
      </div>
      <div className="preview-modal-footer">
        {/* 並び順: 左右アイコン（リサイズグリップ）を一番左 → 実行/停止/リセット。
            制御は共有 context（usePreviewControl）経由で PreviewPanel に委譲。 */}
        <span
          className="preview-modal-resize-grip"
          aria-hidden="true"
          title="おおきさ を かえる"
        >
          <MdOpenInFull size={20} />
        </span>
        <PreviewRunControls />
        <span className="preview-modal-spacer" />
        <div className="preview-modal-actions">
          {devToolsEnabled && ready && scene.avatar && (
            <button
              type="button"
              className="preview-modal-control-button"
              onClick={() => panelRef.current?.cycleCamera()}
              aria-label={`してん きりかえ（いま: ${CAMERA_MODE_LABEL[cameraMode]}）`}
              title="してん きりかえ (V キー)"
            >
              {CAMERA_MODE_LABEL[cameraMode]}
            </button>
          )}
          {pipSupported && !pipWindow && (
            <button
              type="button"
              className="preview-modal-action-button"
              onClick={enterPip}
              aria-label="ピクチャー・イン・ピクチャーで ひらく"
              title="べつウィンドウで ひらく"
            >
              <MdOpenInNew size={20} aria-hidden="true" />
            </button>
          )}
          {pipWindow && (
            <button
              type="button"
              className="preview-modal-action-button"
              onClick={exitPip}
              aria-label="べつウィンドウを とじる"
              title="べつウィンドウを とじる"
            >
              <MdCloseFullscreen size={20} aria-hidden="true" />
            </button>
          )}
          {/* 最小化はメイン窓内の機能（右端タブへ収納）。PIP 窓では機能しない
              ため表示しない。 */}
          {!pipWindow && (
            <button
              type="button"
              className="preview-modal-action-button"
              onClick={minimize}
              aria-label="さいしょうか"
              title="さいしょうか"
            >
              <MdRemove size={22} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  )

  // PreviewPanel 本体は安定したホスト div へ一度だけ portal する。content の portal
  // 先（メイン窓 / PIP 窓）が変わってもこの portal は維持され、再マウントされない。
  const panelPortal = createPortal(
    <PreviewPanel
      ref={panelRef}
      key={panelKey}
      scene={scene}
      workspaces={workspaces}
      autoRun={false}
      showControls={false}
      cameraSwitchEnabled={devToolsEnabled}
      onReady={handleReady}
      onCameraModeChange={setCameraMode}
      onRunningChange={setControlRunning}
      onVolumeChange={setControlVolume}
    />,
    panelHost,
  )

  if (pipWindow) {
    return (
      <>
        {panelPortal}
        {createPortal(content, pipWindow.document.body)}
      </>
    )
  }

  return (
    <>
      {panelPortal}
      <Rnd
        style={{
          // 動画プレイヤーと同じ z 層を基準に、最後に操作した窓を前面へ出す。
          zIndex: `calc(var(--z-video-window) + ${order})`,
          visibility: minimized ? 'collapse' : 'visible',
        }}
        size={size}
        position={position}
        minWidth={MIN_WIDTH}
        minHeight={MIN_HEIGHT}
        bounds="window"
        disableDragging
        enableResizing={RESIZE_ENABLE}
        resizeHandleStyles={RESIZE_HANDLE_STYLES}
        onMouseDown={bringToFront}
        onResizeStart={bringToFront}
        onResize={handleResize}
      >
        {content}
      </Rnd>
      {minimized && <PreviewMinimizedTab onClick={restore} />}
    </>
  )
}
